import { getCookie, setCookie } from '@tencent/halo-utils';
import { PcLogin } from '@tencent/syzs-login-sdk';
import { Ipc, Market, Window } from './core/index';
import { isAppMarket, jsonp } from './core/utils';
import { LoginInfo, LoginType } from './data';

/** Toast 可选参数 */
export interface ToastOptions {
  [x: string]: any;

  /** 展示时长 (毫秒) */
  duration?: number;
}

/** 图片查看器选项 */
export interface ShowPicturesOptions {
  [x: string]: any;

  /** 初始显示哪张图片, 默认为 0（首张） */
  index?: number;
}

/** 打开 WebView 可选参数 */
export interface OpenWebViewOptions {
  [x: string]: any;
}

/** 关闭 WebView 可选参数 */
export interface CloseWebViewOptions {
  [x: string]: any;
}

/** 分享可选参数 */
export interface ShareOptions {
  [x: string]: any;
}

/** 登录可选参数 */
export interface LoginOptions {
  [x: string]: any;

  /**
   * 主动选择以哪种方式登录
   *
   * @description
   * 当传入 unknown 或是不传入时，不限定用户可用的登录类型。
   * 如果请求一个当前媒体端不支持的登录类型将会抛出异常。
   */
  loginType?: LoginType;
}

export interface UIInterface {
  /**
   * 弹出一个纯文本的 Toast
   * @param message Toast 内容
   * @param options Toast 额外选项
   * @description 在不指定 Toast 展示时长的情况下不同媒体端可能会有自己不同的展示时长
   */
  toast(message: string, options?: ToastOptions): Promise<void>;

  /**
   * 调起图片查看器
   * @param pictures 要展示的图片 URL 列表
   * @param options 图片查看器选项
   */
  showPictures(pictures: string[], options?: ShowPicturesOptions): Promise<void>;

  /**
   * 在一个新的 WebView 窗口打开链接
   *
   * @description
   * 由于不同媒体端的 API 差异较大，这里只定义了最基本的功能，并提供一个 options 入口，
   * 可以传入不同媒体端的自定义参数。不支持打开新 Webview 的媒体端将会直接在当前 Webview 中打开。
   */
  openWebView(url: string, options?: OpenWebViewOptions): Promise<void>;

  /**
   * 关闭当前 WebView
   *
   * @description
   * 当前 Webview 指调用本接口的 Webview，不一定是当前可见的 Webview。
   *
   * 由于不同媒体端的 API 差异较大，这里只定义了最基本的功能，
   * 并提供一个 options 入口，可以传入不同媒体端的自定义参数。
   */
  closeWebView(options?: CloseWebViewOptions): Promise<void>;

  /**
   * 分享当前页面
   *
   * @description
   * 根据媒体端的不同差异可能较大，有可能是没有任何行为、弹出分享浮层、弹出好友选择框等。
   * 这里只定义了最基本的功能，并提供一个 options 入口，可以传入不同媒体端的自定义参数。
   */
  share(options?: ShareOptions): Promise<void>;

  /**
   * 唤起客户端登录界面
   * @description
   */
  login(options?: LoginOptions): Promise<void>;
}

/**
 * 向市场主页面发送消息
 * @param msg
 */
async function sendMessageToMain<T>(msg: LoginMessage<T>) {
  if (isAppMarket()) {
    // 通知市场主页面处理登录状态
    const info = await Window.GetOtherWindowInfo({ name: 'market' });

    // 端内用ipc能力
    if (info?.browserId) {
      await Ipc.SendMessage(info.browserId, JSON.stringify(msg));
    }

    return;
  }

  // 端外postmessage
  window.top.postMessage({
    ...msg,
    from: 'jsbridge',
  }, '*');
}

function loginChina(options: LoginOptions) {
  const DIV_ID = 'syzs-login';
  const LOCAL_FLAG = 'syzs-jsbride-last-loginType';
  let type = options.loginType;
  const selector = options.selector || `#${DIV_ID}`;

  if (!type) {
    type = localStorage.getItem(LOCAL_FLAG) as LoginType || undefined;
  }

  let dom: HTMLElement = document.querySelector(selector);
  if (!dom) {
    // 如果传入了selector但是没找到,抛出异常
    if (options.selector) throw new Error(`cannot find selector <${options.selector}>`);

    dom = document.createElement('div');
    dom.id = DIV_ID;
    const style = document.createElement('style');

    style.innerHTML = `
    ${selector}{
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 20000;
    color: #000;
    background-color: rgba(0,0,0,.8);
    }`;

    document.head.appendChild(style);
    document.body.appendChild(dom);
  }

  dom.style.display = 'block';

  return new Promise<void>((resolve, reject) => {
    const pcLogin = new PcLogin({
      root: dom,
      type: {
        QC: 'qq',
        WX: 'wx',
      }[(type?.toString()) as any],
      async onSucess() {
        // 登录成功回调
        const loginType: LoginType = getCookie('logintype') || '' as any;
        await getUserInfo();

        try {
          await sendMessageToMain<LoginInfo>({
            type: IpcLoginEvent.Login,
          });
        } catch (e) {
          console.log(e);
          reject(e);
        }

        localStorage.setItem(LOCAL_FLAG, loginType);

        pcLogin.destroy();
        document.querySelector(selector)!.style.display = 'none';
        resolve();
      },
      onCancel() {
        document.querySelector(selector)!.style.display = 'none';
        reject(new Error('user cancel'));
      },
    });
    pcLogin.show();
  });
}

function setCookieWrap(key: string, val: string) {
  setCookie(key, val, {
    domain: 'qq.com',
    path: '/',
    expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30),
  });
}

/**
 * 获取用户头像及昵称
 * @returns
 */
export function getUserInfo(): Promise<Partial<LoginInfo>> {
  return jsonp('https://sy.guanjia.qq.com/api/auth/userinfo').then((res: any) => {
    if (res.code === 0 && res.data) {
      setCookieWrap('nickname', res.data.nickname);
      setCookieWrap('headImg', res.data.headimgurl);
      // 兼容老版本字段
      setCookieWrap('headimgurl', res.data.headimgurl);

      return {
        nickname: res.data.nickname,
        headImg: res.data.headimgurl,
      };
    }

    throw new Error(`getUserInfo error:${JSON.stringify(res)}`);
  });
}

export enum IpcLoginEvent {
  Login = 'login',
  Logout = 'logout'
}
export interface LoginMessage<T>{
  type: IpcLoginEvent;
  data?: T;
}
/**
 * 唤起客户端登录界面
 * @description
 */
export function login(options: LoginOptions = {}): Promise<void> {
  const { region = 'CN' } = options;

  if (!region || region === 'CN') {
    return loginChina({
      ...options,
      region,
    });
  }

  return Promise.resolve();
}

export async function logout(): Promise<void> {
  Market.Logout(0);
  // 设置时间为三天前
  const expiredTime = new Date(Date.now() - 1000 * 60 * 60 * 24 * 3);

  function delCookie(key: string, domain: string, path: string) {
    setCookie(key, '', {
      expires: expiredTime,
      path,
      domain,
    });
  }

  // 清空登录态
  delCookie('nickname', 'qq.com', '/');
  delCookie('uin', 'qq.com', '/');
  delCookie('logintype', 'qq.com', '/');
  delCookie('skey', 'qq.com', '/');
  delCookie('gender', 'qq.com', '/');
  delCookie('headimgurl', 'qq.com', '/');
  delCookie('headImg', 'qq.com', '/');
  delCookie('openid', 'qq.com', '/');
  delCookie('accesstoken', 'qq.com', '/');
  delCookie('refreshtoken', 'qq.com', '/');
  delCookie('wx_gender', 'qq.com', '/');
  delCookie('yopenid', 'qq.com', '/');
  delCookie('seckey', 'qq.com', '/');
  delCookie('yy_openid', 'qq.com', '/');
  delCookie('yy_logintype', 'qq.com', '/');
  delCookie('yy_token', 'qq.com', '/');

  // 派发登出事件
  await sendMessageToMain({
    type: IpcLoginEvent.Logout,
  });
}
